(() => {
  const ROOT = '[data-deeb-event-root]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();

  class EventPage {
    constructor(root) {
      this.root = root;
      this.hero = root.querySelector('[data-deeb-event-hero]');
      this.flow = root.querySelector('[data-deeb-event-flow]');
      this.flowSource = root.querySelector('[data-deeb-event-flow-source]');
      this.flowSequence = root.querySelector('[data-deeb-event-flow-sequence]');
      this.flowSteps = [];
      this.flowStage = root.querySelector('.deeb-event__flow-stage');
      this.flowPath = root.querySelector('.deeb-event__flow-map-progress');
      this.flowPathBase = root.querySelector('.deeb-event__flow-map-base');
      this.flowTraveler = null;
      this.flowGhost = null;
      this.flowGeometryKey = '';
      this.flowSamples = [];
      this.flowStops = [];
      this.flowMetrics = null;
      this.flowActiveIndex = -1;
      this.flowRenderedProgress = -1;
      this.flowComplete = false;
      this.flowLayoutRaf = 0;
      this.flowResizeObserver = null;
      this.heroMetrics = null;
      this.heroRenderedProgress = -1;
      this.desktopFlowMedia = window.matchMedia('(min-width: 990px)');
      this.mobileGalleryMedia = window.matchMedia('(max-width: 749px)');
      this.gallery = root.querySelector('[data-deeb-event-gallery]');
      this.galleryFrames = [...root.querySelectorAll('[data-deeb-event-gallery-frame]')];
      this.galleryCurrent = root.querySelector('[data-deeb-event-gallery-current]');
      this.galleryTotal = root.querySelector('[data-deeb-event-gallery-total]');
      this.galleryPrev = root.querySelector('[data-deeb-event-gallery-prev]');
      this.galleryNext = root.querySelector('[data-deeb-event-gallery-next]');
      this.galleryActiveIndex = 0;
      this.revealObserver = null;
      this.raf = 0;
      this.galleryRaf = 0;
      this.dragging = false;
      this.pointerId = null;
      this.dragStartX = 0;
      this.dragStartScroll = 0;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onGalleryScroll = this.onGalleryScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPrevClick = () => this.scrollGallery(-1);
      this.onNextClick = () => this.scrollGallery(1);
      this.init();
    }

    init() {
      this.root.classList.add('is-ready');
      this.initReveal();
      this.buildFlowSequence();
      this.initFlowJourney();
      this.initGallery();
      this.initMotion();
    }

    initReveal() {
      const items = [...this.root.querySelectorAll('[data-deeb-event-reveal]')];
      if (!items.length) return;

      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -7% 0px', threshold: 0.08 },
      );

      items.forEach((item) => this.revealObserver.observe(item));
    }

    parseFlowFallback(richText) {
      if (!richText) return [];

      const normalize = (value) => (value || '')
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+/g, ' ')
        .trim();

      const fullText = richText.innerText || richText.textContent || '';
      const lines = fullText
        .split(/\r?\n+/)
        .map(normalize)
        .filter(Boolean);

      if (!lines.length) return [];

      // The current Event flow entry is authored as four named moments. Shopify can
      // serialize Rich Text as one paragraph, several paragraphs, line breaks, or a
      // bold stage label at the start of a paragraph. Handle the block-prefix form
      // before falling back to flattened line parsing.
      const canonicalTitles = ['Arrival', 'Cigar selection', 'Hospitality', 'The evening'];
      const blockTexts = [...richText.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li')]
        .map((node) => normalize(node.innerText || node.textContent || ''))
        .filter(Boolean);
      const prefixedPairs = blockTexts
        .map((block) => {
          const title = canonicalTitles.find((candidate) => {
            const lowerBlock = block.toLowerCase();
            const lowerTitle = candidate.toLowerCase();
            return lowerBlock === lowerTitle || lowerBlock.startsWith(`${lowerTitle} `);
          });
          if (!title) return null;
          return { title, text: normalize(block.slice(title.length)) };
        })
        .filter(Boolean);

      if (prefixedPairs.length >= 2) return prefixedPairs;

      const canonicalIndexes = canonicalTitles
        .map((title) => lines.findIndex((line) => line.toLowerCase() === title.toLowerCase()));

      if (canonicalIndexes.filter((index) => index >= 0).length >= 2) {
        const pairs = [];
        canonicalTitles.forEach((title, titleIndex) => {
          const start = canonicalIndexes[titleIndex];
          if (start < 0) return;

          const later = canonicalIndexes
            .slice(titleIndex + 1)
            .filter((index) => index > start);
          const end = later.length ? Math.min(...later) : lines.length;
          const text = normalize(lines.slice(start + 1, end).join(' '));
          pairs.push({ title, text });
        });
        if (pairs.length) return pairs;
      }

      // Generic fallback for future events before structured Event Stage entries are
      // introduced: preserve authored block order and pair title/description lines.
      const blockLines = [...richText.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li')]
        .flatMap((node) => (node.innerText || node.textContent || '').split(/\r?\n+/))
        .map(normalize)
        .filter(Boolean);
      const raw = blockLines.length >= 2 ? blockLines : lines;
      const pairs = [];

      for (let index = 0; index < raw.length; index += 2) {
        const title = raw[index];
        const text = raw[index + 1] || '';
        if (title) pairs.push({ title, text });
      }

      return pairs;
    }

    buildFlowSequence() {
      if (!this.flow || !this.flowSequence) return;

      const serverSteps = [...this.flowSequence.querySelectorAll('[data-deeb-flow-step]')];
      if (serverSteps.length) {
        this.flowSteps = serverSteps;
        this.flowSequence.removeAttribute('aria-hidden');
        this.flow.classList.add('is-flow-enhanced');
        this.updateFlow(window.innerHeight || document.documentElement.clientHeight);
        return;
      }

      if (!this.flowSource) return;

      const richText = this.flowSource.querySelector('.metafield-rich_text_field') || this.flowSource;
      const pairs = this.parseFlowFallback(richText);
      if (!pairs.length) return;

      const fallbackCues = ['Guest welcome', 'Curated offer', 'Guided service', 'Shared atmosphere'];
      this.flowSequence.replaceChildren();

      pairs.slice(0, 6).forEach((step, index) => {
        const article = document.createElement('article');
        article.className = 'deeb-event__flow-step';
        article.dataset.deebFlowStep = String(index);
        article.style.setProperty('--flow-index', String(index));

        const station = document.createElement('div');
        station.className = 'deeb-event__flow-station';
        station.setAttribute('aria-hidden', 'true');

        const shape = document.createElement('span');
        shape.className = `deeb-event__flow-shape deeb-event__flow-shape--${(index % 4) + 1}`;
        shape.innerHTML = '<i></i><b></b>';

        const number = document.createElement('em');
        number.textContent = String(index + 1).padStart(2, '0');
        station.append(shape, number);

        const copy = document.createElement('div');
        copy.className = 'deeb-event__flow-copy';

        if (fallbackCues[index]) {
          const cue = document.createElement('small');
          cue.textContent = fallbackCues[index];
          copy.append(cue);
        }

        const heading = document.createElement('h3');
        heading.textContent = step.title;
        const paragraph = document.createElement('p');
        paragraph.textContent = step.text;
        copy.append(heading, paragraph);

        article.append(station, copy);
        this.flowSequence.append(article);
      });

      this.flowSequence.removeAttribute('aria-hidden');
      this.flowSource.setAttribute('aria-hidden', 'true');
      this.flowSteps = [...this.flowSequence.querySelectorAll('[data-deeb-flow-step]')];
      this.flow.classList.add('is-flow-enhanced', 'is-flow-fallback-built');
      this.updateFlow(window.innerHeight || document.documentElement.clientHeight);
    }


    initFlowJourney() {
      if (!this.flow || !this.flowStage || !this.flowSteps.length) return;

      const traveler = document.createElement('span');
      traveler.className = 'deeb-event__flow-traveler';
      traveler.setAttribute('aria-hidden', 'true');
      this.flowStage.append(traveler);
      this.flowTraveler = traveler;

      const ghost = document.createElement('span');
      ghost.className = 'deeb-event__flow-ghost';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.textContent = '01';
      this.flowStage.append(ghost);
      this.flowGhost = ghost;

      if ('ResizeObserver' in window) {
        this.flowResizeObserver = new ResizeObserver(() => this.scheduleFlowLayout());
        this.flowResizeObserver.observe(this.flowStage);
      }

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.scheduleFlowLayout());
      }

      this.scheduleFlowLayout();
    }

    scheduleFlowLayout() {
      if (this.flowLayoutRaf) return;
      this.flowLayoutRaf = window.requestAnimationFrame(() => {
        this.flowLayoutRaf = 0;
        this.refreshMotionLayout();
      });
    }

    refreshHeroMetrics() {
      if (!this.hero) {
        this.heroMetrics = null;
        return;
      }

      const rect = this.hero.getBoundingClientRect();
      this.heroMetrics = {
        top: rect.top + window.scrollY,
        height: Math.max(1, rect.height),
      };
    }

    getFlowStationCenter(step, stageRect) {
      if (!step || !this.flowStage || !stageRect) return null;
      const target = step.querySelector('.deeb-event__flow-shape') || step;
      const targetRect = target.getBoundingClientRect();
      return {
        x: targetRect.left + (targetRect.width / 2) - stageRect.left,
        y: targetRect.top + (targetRect.height / 2) - stageRect.top,
      };
    }

    buildFlowPath(points) {
      if (!points.length) return '';
      if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

      const parts = [`M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`];
      for (let index = 0; index < points.length - 1; index += 1) {
        const p0 = points[index - 1] || points[index];
        const p1 = points[index];
        const p2 = points[index + 1];
        const p3 = points[index + 2] || p2;
        const cp1 = {
          x: p1.x + ((p2.x - p0.x) / 6),
          y: p1.y + ((p2.y - p0.y) / 6),
        };
        const cp2 = {
          x: p2.x - ((p3.x - p1.x) / 6),
          y: p2.y - ((p3.y - p1.y) / 6),
        };
        parts.push(
          `C ${cp1.x.toFixed(2)} ${cp1.y.toFixed(2)} ${cp2.x.toFixed(2)} ${cp2.y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
        );
      }
      return parts.join(' ');
    }

    cacheFlowPath(points) {
      if (!this.flowPath || !points.length) {
        this.flowSamples = [];
        this.flowStops = [];
        return;
      }

      const totalLength = this.flowPath.getTotalLength();
      if (!Number.isFinite(totalLength) || totalLength <= 0) {
        this.flowSamples = [];
        this.flowStops = [];
        return;
      }

      const sampleCount = 480;
      const samples = [];
      for (let index = 0; index <= sampleCount; index += 1) {
        const progress = index / sampleCount;
        const point = this.flowPath.getPointAtLength(totalLength * progress);
        samples.push({ x: point.x, y: point.y, progress });
      }

      const stops = [];
      let searchStart = 0;
      points.forEach((target, pointIndex) => {
        let bestIndex = searchStart;
        let bestDistance = Infinity;
        for (let index = searchStart; index < samples.length; index += 1) {
          const sample = samples[index];
          const distance = ((sample.x - target.x) ** 2) + ((sample.y - target.y) ** 2);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        }
        stops.push(samples[bestIndex].progress);
        searchStart = bestIndex;
        if (pointIndex === 0) stops[0] = 0;
      });

      if (stops.length) stops[stops.length - 1] = 1;
      this.flowSamples = samples;
      this.flowStops = stops;
    }

    refreshFlowGeometry() {
      if (!this.desktopFlowMedia.matches || reduceMotion.matches) {
        this.flowMetrics = null;
        return;
      }
      if (!this.flow || !this.flowStage || !this.flowPath || !this.flowPathBase || !this.flowSteps.length) return;

      const stageRect = this.flowStage.getBoundingClientRect();
      if (stageRect.width <= 0 || stageRect.height <= 0) return;

      const points = this.flowSteps
        .map((step) => this.getFlowStationCenter(step, stageRect))
        .filter(Boolean);
      if (points.length !== this.flowSteps.length) return;

      const geometryKey = [
        Math.round(stageRect.width),
        Math.round(stageRect.height),
        ...points.flatMap((point) => [Math.round(point.x), Math.round(point.y)]),
      ].join(':');

      if (geometryKey !== this.flowGeometryKey) {
        this.flowGeometryKey = geometryKey;
        const svg = this.flowPath.ownerSVGElement;
        svg?.setAttribute('viewBox', `0 0 ${stageRect.width.toFixed(2)} ${stageRect.height.toFixed(2)}`);
        svg?.setAttribute('preserveAspectRatio', 'none');

        const pathData = this.buildFlowPath(points);
        this.flowPathBase.setAttribute('d', pathData);
        this.flowPath.setAttribute('d', pathData);
        this.flowPath.style.strokeDasharray = '1';
        this.flowPath.style.strokeDashoffset = '1';
        this.cacheFlowPath(points);
        this.flowRenderedProgress = -1;
      }

      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const flowRect = this.flow.getBoundingClientRect();
      this.flowMetrics = {
        top: flowRect.top + window.scrollY,
        travel: Math.max(1, this.flow.offsetHeight - viewport),
        viewport,
      };
    }

    refreshMotionLayout() {
      this.refreshHeroMetrics();
      this.refreshFlowGeometry();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      this.updateHero(viewport, window.scrollY);
      this.updateFlow(viewport, window.scrollY);
    }

    getCachedFlowPoint(progress) {
      if (!this.flowSamples.length) return null;
      const clamped = Math.max(0, Math.min(1, progress));
      const scaled = clamped * (this.flowSamples.length - 1);
      const lowerIndex = Math.floor(scaled);
      const upperIndex = Math.min(this.flowSamples.length - 1, lowerIndex + 1);
      const mix = scaled - lowerIndex;
      const lower = this.flowSamples[lowerIndex];
      const upper = this.flowSamples[upperIndex];
      return {
        x: lower.x + ((upper.x - lower.x) * mix),
        y: lower.y + ((upper.y - lower.y) * mix),
      };
    }

    getFlowActiveIndex(progress) {
      if (!this.flowStops.length) {
        return Math.min(
          this.flowSteps.length - 1,
          Math.max(0, Math.floor(progress * this.flowSteps.length)),
        );
      }

      let activeIndex = 0;
      this.flowStops.forEach((stop, index) => {
        if (progress + 0.0005 >= stop) activeIndex = index;
      });
      return activeIndex;
    }

    setFlowActiveIndex(activeIndex) {
      if (activeIndex === this.flowActiveIndex) return;
      this.flowActiveIndex = activeIndex;

      this.flowSteps.forEach((step, index) => {
        step.classList.toggle('is-active', index <= activeIndex);
        step.classList.toggle('is-current', index === activeIndex);
      });

      if (this.flowGhost) {
        this.flowGhost.textContent = String(Math.max(0, activeIndex) + 1).padStart(2, '0');
      }
    }

    renderDesktopFlow(progress, activeIndex, complete) {
      if (!this.desktopFlowMedia.matches || !this.flowPath) return;
      const clamped = Math.max(0, Math.min(1, progress));

      if (Math.abs(clamped - this.flowRenderedProgress) > 0.0002) {
        this.flowRenderedProgress = clamped;
        this.flow.style.setProperty('--flow-progress', clamped.toFixed(4));
        this.flowPath.style.strokeDashoffset = (1 - clamped).toFixed(4);

        const point = this.getCachedFlowPoint(clamped);
        if (point && this.flowTraveler) {
          this.flowTraveler.style.transform = `translate3d(${point.x.toFixed(2)}px, ${point.y.toFixed(2)}px, 0) translate(-50%, -50%)`;
        }
      }

      this.setFlowActiveIndex(activeIndex);
      if (complete !== this.flowComplete) {
        this.flowComplete = complete;
        this.flow.classList.toggle('is-flow-complete', complete);
      }
    }

    initGallery() {
      if (!this.gallery || !this.galleryFrames.length) return;

      if (this.galleryTotal) {
        this.galleryTotal.textContent = String(this.galleryFrames.length).padStart(2, '0');
      }

      this.gallery.addEventListener('scroll', this.onGalleryScroll, { passive: true });
      this.gallery.addEventListener('pointerdown', this.onPointerDown);
      this.gallery.addEventListener('pointermove', this.onPointerMove);
      this.gallery.addEventListener('pointerup', this.onPointerUp);
      this.gallery.addEventListener('pointercancel', this.onPointerUp);
      this.gallery.addEventListener('keydown', this.onKeyDown);
      this.galleryPrev?.addEventListener('click', this.onPrevClick);
      this.galleryNext?.addEventListener('click', this.onNextClick);
      this.updateGalleryActive();
    }

    onGalleryScroll() {
      if (this.galleryRaf) return;
      this.galleryRaf = window.requestAnimationFrame(() => {
        this.galleryRaf = 0;
        this.updateGalleryActive();
      });
    }

    updateGalleryActive() {
      if (!this.gallery || !this.galleryFrames.length) return;

      let active = 0;
      let distance = Infinity;

      if (this.mobileGalleryMedia.matches) {
        // Mobile uses the browser's native horizontal scrolling + scroll-snap.
        // Use layout offsets instead of viewport geometry reads so the counter
        // stays cheap while the user swipes.
        const target = this.gallery.scrollLeft + (this.gallery.clientWidth / 2);
        this.galleryFrames.forEach((frame, index) => {
          const center = frame.offsetLeft + (frame.offsetWidth / 2);
          const nextDistance = Math.abs(center - target);
          if (nextDistance < distance) {
            distance = nextDistance;
            active = index;
          }
        });
      } else {
        const galleryRect = this.gallery.getBoundingClientRect();
        const target = galleryRect.left + galleryRect.width * 0.52;
        this.galleryFrames.forEach((frame, index) => {
          const rect = frame.getBoundingClientRect();
          const center = rect.left + rect.width / 2;
          const nextDistance = Math.abs(center - target);
          if (nextDistance < distance) {
            distance = nextDistance;
            active = index;
          }
        });
      }

      this.galleryActiveIndex = active;
      this.galleryFrames.forEach((frame, index) => frame.classList.toggle('is-active', index === active));
      if (this.galleryCurrent) this.galleryCurrent.textContent = String(active + 1).padStart(2, '0');
    }

    scrollGallery(direction) {
      if (!this.gallery || !this.galleryFrames.length) return;
      const count = this.galleryFrames.length;
      const nextIndex = (this.galleryActiveIndex + direction + count) % count;
      const frame = this.galleryFrames[nextIndex];
      frame?.scrollIntoView({
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    initMotion() {
      if (reduceMotion.matches || (!this.hero && !this.flow)) return;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.scheduleFlowLayout();
      this.onScroll();
    }

    updateHero(viewport, scrollY = window.scrollY) {
      if (!this.hero) return;
      if (!this.heroMetrics) this.refreshHeroMetrics();
      if (!this.heroMetrics) return;

      const progress = Math.max(
        0,
        Math.min(1, (scrollY - this.heroMetrics.top) / this.heroMetrics.height),
      );
      if (Math.abs(progress - this.heroRenderedProgress) <= 0.0005) return;
      this.heroRenderedProgress = progress;
      this.hero.style.setProperty('--event-hero-progress', progress.toFixed(4));
    }

    updateFlow(viewport, scrollY = window.scrollY) {
      if (!this.flow || !this.flowSteps.length) return;

      if (this.desktopFlowMedia.matches && !reduceMotion.matches) {
        if (!this.flowMetrics || !this.flowSamples.length) {
          this.scheduleFlowLayout();
          return;
        }

        const rawProgress = Math.max(
          0,
          Math.min(1, (scrollY - this.flowMetrics.top) / this.flowMetrics.travel),
        );

        // Finish the route at 86% of the pinned corridor and hold the completed
        // blueprint for the final 14% before releasing to the next section.
        const journeyProgress = Math.max(0, Math.min(1, rawProgress / 0.86));
        const activeIndex = this.getFlowActiveIndex(journeyProgress);
        this.renderDesktopFlow(
          journeyProgress,
          activeIndex,
          activeIndex === this.flowSteps.length - 1 && rawProgress >= 0.86,
        );
        return;
      }

      const rect = this.flow.getBoundingClientRect();
      const start = viewport * 0.8;
      const travel = Math.max(1, rect.height + viewport * 0.25);
      const progress = Math.max(0, Math.min(1, (start - rect.top) / travel));
      const activeIndex = Math.min(
        this.flowSteps.length - 1,
        Math.max(0, Math.floor(progress * this.flowSteps.length)),
      );

      this.flow.style.setProperty('--flow-progress', progress.toFixed(4));
      this.flow.classList.toggle(
        'is-flow-complete',
        activeIndex === this.flowSteps.length - 1 && progress >= 0.82,
      );
      this.flowSteps.forEach((step, index) => step.classList.toggle('is-active', index <= activeIndex));
    }

    onPointerDown(event) {
      if (!this.gallery) return;
      // Do not emulate touch dragging on phones. Native overflow scrolling is
      // smoother, preserves vertical page scrolling, and lets CSS scroll-snap
      // settle the frame without pointer capture fighting the browser.
      if (this.mobileGalleryMedia.matches && event.pointerType !== 'mouse') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.dragging = true;
      this.pointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.gallery.scrollLeft;
      this.gallery.classList.add('is-dragging');
      this.gallery.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.gallery || !this.dragging || event.pointerId !== this.pointerId) return;
      this.gallery.scrollLeft = this.dragStartScroll - (event.clientX - this.dragStartX);
    }

    onPointerUp(event) {
      if (!this.gallery || !this.dragging || event.pointerId !== this.pointerId) return;
      this.gallery.releasePointerCapture?.(event.pointerId);
      this.dragging = false;
      this.pointerId = null;
      this.gallery.classList.remove('is-dragging');
      this.updateGalleryActive();
    }

    onKeyDown(event) {
      if (!this.gallery || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.scrollGallery(event.key === 'ArrowRight' ? 1 : -1);
    }

    onScroll() {
      if (this.raf) return;
      this.raf = window.requestAnimationFrame(() => {
        this.raf = 0;
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        const scrollY = window.scrollY;
        this.updateHero(viewport, scrollY);
        this.updateFlow(viewport, scrollY);
      });
    }

    onResize() {
      this.heroMetrics = null;
      this.flowMetrics = null;
      this.scheduleFlowLayout();
    }

    destroy() {
      this.revealObserver?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.flowResizeObserver?.disconnect();
      this.gallery?.removeEventListener('scroll', this.onGalleryScroll);
      this.gallery?.removeEventListener('pointerdown', this.onPointerDown);
      this.gallery?.removeEventListener('pointermove', this.onPointerMove);
      this.gallery?.removeEventListener('pointerup', this.onPointerUp);
      this.gallery?.removeEventListener('pointercancel', this.onPointerUp);
      this.gallery?.removeEventListener('keydown', this.onKeyDown);
      this.galleryPrev?.removeEventListener('click', this.onPrevClick);
      this.galleryNext?.removeEventListener('click', this.onNextClick);
      if (this.raf) window.cancelAnimationFrame(this.raf);
      if (this.galleryRaf) window.cancelAnimationFrame(this.galleryRaf);
      if (this.flowLayoutRaf) window.cancelAnimationFrame(this.flowLayoutRaf);
      controllers.delete(this.root);
    }
  }

  const init = (scope = document) => {
    const roots = scope.matches?.(ROOT) ? [scope] : [...(scope.querySelectorAll?.(ROOT) || [])];
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new EventPage(root));
    });
  };

  init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT) ? event.target : event.target.querySelector?.(ROOT);
    if (root) controllers.get(root)?.destroy();
  });
})();
